// components/CustomerTable.js
import React, { useEffect } from 'react';
import { Pagination } from 'semantic-ui-react';
import {
  formatCallGroupMemberStatus,
  formatCallGroupPriorityCell,
  formatCallGroupRequiredCell,
  formatCallGroupNameCell,
  callGroupEscalationColor,
  formatOutcomeStatus,
} from './callGroupCustomerFormat';
import { DatatablePageSizeSelect } from '../../CompletedChats/DatatablePageSizeSelect';
import { filterCampaignCustomers, useDebouncedValue } from './filterCampaignCustomers';

const CustomerTable = ({
  customers,
  customersPage,
  customersPerPage,
  getTotalCustomers,
  viewCustomer,
  setCustomersPage,
  onOptOutChange,
  isCampaignSaved = false,
  /** When true, match CampaignDetails columns (Priority, Required, Group name, Escalation; Status → Outcome). */
  showCallGroupColumns = false,
  /** Bulk delete UI; omit to hide (CampaignForm always passes this from the bulk hook). */
  bulkSelection = null,
  customerSearch = '',
  onCustomerSearchChange,
  onCustomersPerPageChange,
}) => {
  const debouncedCustomerSearch = useDebouncedValue(customerSearch);
  const filteredCustomers = filterCampaignCustomers(customers, debouncedCustomerSearch);
  const totalFiltered = filteredCustomers.length;
  const totalPages = Math.max(1, Math.ceil(totalFiltered / customersPerPage) || 1);
  const activePage = Math.min(customersPage, totalPages);
  const start = (activePage - 1) * customersPerPage;
  const end = start + customersPerPage;
  const pageSlice = filteredCustomers.slice(start, end);

  useEffect(() => {
    setCustomersPage(1);
  }, [debouncedCustomerSearch, customersPerPage, setCustomersPage]);

  useEffect(() => {
    if (customersPage > totalPages) {
      setCustomersPage(totalPages);
    }
  }, [totalPages, customersPage, setCustomersPage]);

  const showBulk = !!bulkSelection;
  const getRowKey = bulkSelection?.getRowKey;
  const selectedKeys = bulkSelection?.selectedKeys ?? [];
  const interactionsEnabled = bulkSelection?.interactionsEnabled ?? false;
  const isLocked = bulkSelection?.isLocked ?? false;

  const selectableRowsOnPage = showBulk
    ? pageSlice.filter((c) => bulkSelection.isRowSelectable(c))
    : [];
  const allRowsOnPageSelected =
    showBulk &&
    getRowKey &&
    selectableRowsOnPage.length > 0 &&
    selectableRowsOnPage.every((c) => selectedKeys.includes(getRowKey(c)));

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return 'N/A';
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const year = date.getFullYear();
      return `${month}/${day}/${year}`;
    } catch (error) {
      return 'N/A';
    }
  };

  return (
    <>
      {getTotalCustomers() > 0 && (
        <div
          className="card shadow-sm p-3"
          style={{
            marginTop: '33px',
            width: isCampaignSaved || showCallGroupColumns ? '100%' : '75%',
          }}
        >
          {showBulk && (
            <h2 className="mb-3">{showCallGroupColumns ? 'Call group contacts' : 'Customers Info'}</h2>
          )}
          {typeof onCustomerSearchChange === 'function' && (
            <div className="customers-info-toolbar">
              <form onSubmit={(e) => e.preventDefault()}>
                <div className="kt-input-icon kt-input-icon--right header-search">
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Search by name, phone, or date..."
                    value={customerSearch}
                    onChange={(e) => {
                      onCustomerSearchChange(e.target.value);
                    }}
                    aria-label="Search customers"
                  />
                  <span className="kt-input-icon__icon kt-input-icon__icon--right">
                    <span><i className="la la-search" aria-hidden="true" /></span>
                  </span>
                </div>
              </form>
              {showBulk && (
                <div className="customers-info-toolbar-actions">
                  <button
                    type="button"
                    className="btn btn-sm"
                    disabled={bulkSelection.deleteDisabled}
                    title="Delete"
                    aria-label="Delete"
                    onClick={() => {
                      if (isLocked || !interactionsEnabled || selectedKeys.length === 0) return;
                      bulkSelection.onBulkDeleteClick();
                    }}
                    style={{
                      border: '1px solid #dc3545',
                      borderRadius: '5px',
                      padding: '8px 10px',
                      minWidth: '38px',
                      fontSize: '16px',
                      lineHeight: 1,
                      background: 'none',
                      color: bulkSelection.deleteDisabled ? 'rgba(220, 53, 69, 0.4)' : '#dc3545',
                      cursor: bulkSelection.deleteDisabled ? 'not-allowed' : 'pointer',
                      filter: bulkSelection.deleteDisabled ? 'blur(0.5px)' : 'none',
                    }}
                  >
                    <i className="fa fa-trash" style={{ fontSize: '25px', margin: '-5px 0px 0px 3px' }} aria-hidden="true" />
                  </button>
                </div>
              )}
            </div>
          )}
          <div
            className="table-responsive campaign-listing-table"
            style={{ height: 'auto', overflowX: 'auto', overflowY: 'visible' }}
          >
            <table className="table table-hover" style={{ minWidth: '100%', width: '100%' }}>
              <thead>
                <tr>
                {showBulk && (
                    <th
                      style={{
                        verticalAlign: 'middle',
                        textAlign: 'center',
                        width: '40px',
                      }}
                    >
                      <input
                        ref={bulkSelection.selectAllCheckboxRef}
                        type="checkbox"
                        checked={allRowsOnPageSelected}
                        onChange={() => bulkSelection.onToggleSelectAll()}
                        disabled={
                          selectableRowsOnPage.length === 0 || !interactionsEnabled
                        }
                        title={
                          !interactionsEnabled && isLocked
                            ? 'Completed campaigns cannot delete contacts'
                            : 'Select all deletable contacts on this page'
                        }
                        aria-label="Select all deletable contacts on this page"
                      />
                    </th>
                  )}
                  <th style={{ verticalAlign: 'middle', textAlign: 'left', whiteSpace: 'nowrap', width: isCampaignSaved ? '18%' : '45%' }}>Name</th>
                  <th style={{ verticalAlign: 'middle', textAlign: 'left', whiteSpace: 'nowrap', width: isCampaignSaved ? '18%' : '40%' }}>Phone</th>
                  {showCallGroupColumns && (
                    <>
                      <th style={{ verticalAlign: 'middle', textAlign: 'left', whiteSpace: 'nowrap', width: '8%', fontSize: 13 }}>
                        Priority
                      </th>
                      <th style={{ verticalAlign: 'middle', textAlign: 'left', whiteSpace: 'nowrap', width: '8%', fontSize: 13 }}>
                        Required
                      </th>
                      <th style={{ verticalAlign: 'middle', textAlign: 'left', whiteSpace: 'nowrap', width: '14%', fontSize: 13 }}>
                        Group name
                      </th>
                      <th style={{ verticalAlign: 'middle', textAlign: 'left', whiteSpace: 'nowrap', width: '11%', fontSize: 13 }}>
                        Escalation
                      </th>
                    </>
                  )}
                  {isCampaignSaved && <th style={{ verticalAlign: 'middle', textAlign: 'left', whiteSpace: 'nowrap', width: '15%' }}>Date Added</th>}
                  {isCampaignSaved && (
                    <th style={{ verticalAlign: 'middle', textAlign: 'left', whiteSpace: 'nowrap', width: '12%' }}>
                      {showCallGroupColumns ? 'Outcome' : 'Status'}
                    </th>
                  )}
                  <th style={{ verticalAlign: 'middle', textAlign: 'left', whiteSpace: 'nowrap', width: isCampaignSaved ? '12%' : '15%' }}>Action</th>
                  {/* column for caller opted out of all calling communication */}
                  {isCampaignSaved && <th style={{ verticalAlign: 'middle', textAlign: 'left', minWidth: '130px', maxWidth: '160px', lineHeight: 1.3, width: '25%', padding: '8px 4px' }}>
                    <div style={{ whiteSpace: 'nowrap' }}>
                      Caller Opted Out of All
                    </div>
                    <div style={{ whiteSpace: 'nowrap' }}>
                      Calling Communication
                    </div>
                  </th>}
                </tr>
              </thead>
              <tbody>
                {pageSlice.length > 0 ? pageSlice.map((customer, index) => (
                  <tr key={customer.id || `pending-${start + index}`}>
                    {showBulk && (
                      <td
                        style={{
                          verticalAlign: 'middle',
                          textAlign: 'center',
                          width: '40px',
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={getRowKey ? selectedKeys.includes(getRowKey(customer)) : false}
                          onChange={() => bulkSelection.onToggleRow(customer)}
                          disabled={
                            !bulkSelection.isRowSelectable(customer) || !interactionsEnabled
                          }
                          title={
                            !interactionsEnabled && isLocked
                              ? 'Completed campaigns cannot delete contacts'
                              : !bulkSelection.isRowSelectable(customer)
                                ? 'Active customers cannot be deleted'
                                : 'Select for bulk delete'
                          }
                          aria-label={`Select ${customer.name || 'contact'} for bulk delete`}
                        />
                      </td>
                    )}
                    <td style={{ verticalAlign: 'middle', textAlign: 'left' }}><strong>{customer.name}</strong></td>
                    <td style={{ verticalAlign: 'middle', textAlign: 'left', fontFamily: 'monospace' }}>
                      {(() => {
                        const digits = String(customer.phone_number || '').replace(/\D/g, '');
                        const display = digits.length === 11 && digits[0] === '1' ? digits.slice(1) : digits;
                        return display ? `+1${display}` : customer.phone_number;
                      })()}
                    </td>
                    {showCallGroupColumns && (
                      <>
                        <td style={{ verticalAlign: 'middle', textAlign: 'left', fontSize: 13 }}>
                          {formatCallGroupPriorityCell(customer)}
                        </td>
                        <td style={{ verticalAlign: 'middle', textAlign: 'left', fontSize: 13 }}>
                          {formatCallGroupRequiredCell(customer)}
                        </td>
                        <td
                          style={{ verticalAlign: 'middle', textAlign: 'left', fontSize: 13 }}
                          title={customer.call_group_name || ''}
                        >
                          {formatCallGroupNameCell(customer)}
                        </td>
                        <td style={{ verticalAlign: 'middle', textAlign: 'left', fontSize: 13 }}>
                          <span
                            style={{
                              color: callGroupEscalationColor(customer.call_group_member_status),
                              fontWeight: 400,
                            }}
                          >
                            {formatCallGroupMemberStatus(customer.call_group_member_status)}
                          </span>
                        </td>
                      </>
                    )}
                    {isCampaignSaved && <td style={{ verticalAlign: 'middle', textAlign: 'left' }}>
                      {formatDate(
                        customer.created_at ||
                          customer.date_added ||
                          customer.pending_date_added
                      )}
                    </td>}
                    {isCampaignSaved && <td style={{ verticalAlign: 'middle', textAlign: 'left' }}>
                      <span style={{
                        color: (() => {
                          const ex = String(customer.execution_status || '').toLowerCase();
                          if (ex === 'called' || ex === 'answered') return '#21ba45';
                          if (ex === 'failed') return '#dc3545';
                          return '#6c757d';
                        })(),
                          fontWeight: 400,
                      }}>
                        {formatOutcomeStatus(customer.execution_status)}
                      </span>
                    </td>}
                    <td style={{ verticalAlign: 'middle', textAlign: 'left' }}>
                      <span style={{ display: 'inline-flex', gap: '8px', justifyContent: 'flex-start' }}>
                      <button
                        onClick={() => viewCustomer && viewCustomer(customer)}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: '#2786fb',
                          cursor: 'pointer',
                          fontSize: '14px',
                        }}
                        title="View customer"
                      >
                        <i className="fa fa-eye"></i>
                      </button>
                      </span>
                    </td>
                    {isCampaignSaved && <td style={{ verticalAlign: 'middle', textAlign: 'left' }}>
                      <div
                        role="switch"
                        aria-checked={!!customer.caller_opted_out}
                        aria-label={
                          customer.caller_opted_out
                            ? 'Caller opted out of all calling communication'
                            : 'Caller can receive calls'
                        }
                        onClick={() =>
                          onOptOutChange &&
                          onOptOutChange(customer, !customer.caller_opted_out)
                        }
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            onOptOutChange &&
                              onOptOutChange(customer, !customer.caller_opted_out);
                          }
                        }}
                        tabIndex={0}
                        style={{
                          width: 44,
                          height: 24,
                          borderRadius: 12,
                          backgroundColor: customer.caller_opted_out ? '#28a745' : '#adb5bd',
                          cursor: onOptOutChange ? 'pointer' : 'default',
                          position: 'relative',
                          transition: 'background-color 0.2s ease',
                          margin: 0,
                        }}
                      >
                        <span
                          style={{
                            position: 'absolute',
                            top: 2,
                            left: customer.caller_opted_out ? 22 : 2,
                            width: 20,
                            height: 20,
                            borderRadius: '50%',
                            backgroundColor: '#fff',
                            boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
                            transition: 'left 0.2s ease',
                          }}
                        />
                      </div>
                    </td>}
                  </tr>
                )) : (
                  <tr>
                    <td colSpan={12} className="text-start text-muted py-4">
                      No record found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="d-flex justify-content-between align-items-center mt-3 campaign-customers-footer">
            <div className="d-flex align-items-center flex-wrap" style={{ gap: 25 }}>
              {typeof onCustomersPerPageChange === 'function' && (
                <small className="text-muted d-flex align-items-center" style={{ whiteSpace: 'nowrap' }}>
                  <span className="mr-1">Show</span>
                  <DatatablePageSizeSelect
                    limit={String(customersPerPage)}
                    onChangeLimit={(e, { value }) => {
                      onCustomersPerPageChange(Number(value) || 10);
                    }}
                  />
                  <span className="ml-1">entries</span>
                </small>
              )}
              <small className="text-muted">
                {totalFiltered === 0
                  ? 'Showing 0 of 0'
                  : `Showing ${start + 1}-${Math.min(end, totalFiltered)} of ${totalFiltered}`}
              </small>
            </div>
            <Pagination
              size="mini"
              activePage={activePage}
              totalPages={totalPages}
              onPageChange={(e, { activePage }) => {
                if (totalFiltered > 0) setCustomersPage(activePage);
              }}
            />
          </div>
        </div>
      )}
    </>
  );
};

export default CustomerTable;
